"use client";

import * as React from "react";
import { Label } from "recharts";

import type { ChartConfig } from "./chart";

/**
 * Shared building blocks for the part-to-whole families (pie, radial), which
 * plot one value per category rather than named series across a category axis.
 */

/**
 * Base props for the families that slice a single measure by category. Colours
 * are never passed here — each slice colour is resolved at render time from the
 * {@link ChartConfig} keyed by the category value, so the palette always lives
 * at the call site.
 */
export type PolarChartProps = {
	/** Row data. Each datum holds a category (`nameKey`) and a value (`dataKey`). */
	data: Array<Record<string, unknown>>;
	/**
	 * Slice styling/labels keyed by the category values (`datum[nameKey]`).
	 * Supplies the palette.
	 */
	config: ChartConfig;
	/** Numeric field on each datum used for the slice size. */
	dataKey: string;
	/** Field on each datum used as the slice category; must match a `config` key. */
	nameKey: string;
	/** Extra classes forwarded to the underlying `ChartContainer`. */
	className?: string;
	/** Formats the slice value shown in the tooltip. */
	valueFormatter?: (value: number | string) => React.ReactNode;
	/** Hides the default hover tooltip (shown by default). */
	hideTooltip?: boolean;
	/** Primary centre text (defaults to the summed total). */
	centerLabel?: React.ReactNode;
	/** Secondary centre text under `centerLabel`. */
	centerSublabel?: React.ReactNode;
};

/** Sums `datum[dataKey]` across the rows, ignoring non-numeric values. */
export function useTotal(
	data: Array<Record<string, unknown>>,
	dataKey: string,
): number {
	return React.useMemo(
		() => data.reduce((sum, datum) => sum + (Number(datum[dataKey]) || 0), 0),
		[data, dataKey],
	);
}

/**
 * Centred label for the hollow middle of a donut or ring. Rendered as a child of
 * the Recharts `Pie`/`PolarRadiusAxis`, which supplies the centre coordinates
 * through the `viewBox`.
 */
export function ChartCenterLabel({
	label,
	sublabel,
}: {
	label: React.ReactNode;
	sublabel?: React.ReactNode;
}) {
	return (
		<Label
			content={({ viewBox }) => {
				if (!viewBox || !("cx" in viewBox) || !("cy" in viewBox)) {
					return null;
				}
				const { cx, cy } = viewBox;
				return (
					<text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
						<tspan
							x={cx}
							y={cy}
							className="fill-neutral-900 text-2xl font-bold"
						>
							{label}
						</tspan>
						{sublabel != null && (
							<tspan
								x={cx}
								y={(cy ?? 0) + 22}
								className="fill-neutral-500 text-xs"
							>
								{sublabel}
							</tspan>
						)}
					</text>
				);
			}}
		/>
	);
}
